import React, { Fragment, useMemo, useRef } from 'react';
import pt from 'prop-types';
import { EasyblocksEditor } from '@swell/easyblocks-editor';
import {
  getEasyblocksPagePropsWithConfigs,
  getEasyblocksComponentDefinitions,
} from '@swell/apps-sdk';
import { initGlobalHandlers } from 'components/storefront/editor/easyblocks-page/parser/utils';
import {
  Global,
  getRootComponent,
  getSectionComponent,
  getBlockComponent,
} from './components';
import AssetLoader from './asset-loader';

import './easyblocks-page.scss';

function getInitialSectionPaths(
  sectionGroups,
  layoutSectionGroups,
  pageContent,
) {
  const initialSectionPaths = new Set();

  sectionGroups.forEach((sectionGroupConfig) => {
    if (!sectionGroupConfig.group) {
      return;
    }

    const { prop } = sectionGroupConfig;
    const [, id] = prop.split('_');
    const sectionGroup = id
      ? layoutSectionGroups.find((sectionGroup) => sectionGroup.id === id)
      : pageContent;

    (sectionGroup.order || []).forEach((sectionId, index) => {
      const section = sectionGroup.sections[sectionId];

      if (!section || section.disabled) {
        return;
      }

      initialSectionPaths.add(`${prop}.${index}`);
    });
  });

  return initialSectionPaths;
}

function getEasyblocksComponents(props, overlayRef) {
  const {
    theme,
    themeGlobals,
    allSections,
    layoutSectionGroups,
    sectionGroups,
    pageContent,
    record,
  } = props;

  theme.setGlobals(themeGlobals);

  if (themeGlobals?.shopify_compatibility && !theme.shopifyCompatibility) {
    theme.shopifyCompatibility = new theme.shopifyCompatibilityClass(theme);
  }

  // globally replace fetch and assign error handler
  initGlobalHandlers(theme, record);

  const initialSectionPaths = getInitialSectionPaths(
    sectionGroups,
    layoutSectionGroups,
    pageContent,
  );
  const assetLoader = new AssetLoader(initialSectionPaths, theme);

  return getEasyblocksComponentDefinitions(
    allSections,
    layoutSectionGroups,
    (type, data) => {
      switch (type) {
        case 'global':
          return Global;
        case 'root':
          return getRootComponent(props, theme, assetLoader);
        case 'pageSection':
          return getSectionComponent(
            props,
            data,
            theme,
            assetLoader,
            overlayRef,
          );
        case 'block':
          return getBlockComponent(theme);
        // not necessary
        case 'layoutSectionGroup':
          return () => Fragment;
        default:
          throw new Error(`Invalid component definition type: ${type}`);
      }
    },
  );
}

function EasyblocksPage(props) {
  const {
    pageId,
    allSections,
    pageSections,
    layoutSectionGroups,
    themeGlobals,
  } = props;

  const overlayRef = useRef(null);

  const easyblocksConfig = useMemo(() => {
    const { easyblocksConfig } = getEasyblocksPagePropsWithConfigs(
      themeGlobals,
      allSections,
      pageSections,
      layoutSectionGroups,
      pageId,
    );

    return easyblocksConfig;
  }, [pageId, themeGlobals, allSections, pageSections, layoutSectionGroups]);

  const components = useMemo(() => {
    return getEasyblocksComponents(props, overlayRef);
  }, [props]);

  return (
    <Fragment>
      <EasyblocksEditor
        config={easyblocksConfig}
        components={components}
        __debug={true}
        isCanvas={true}
      />
      <div id="easyblocks-page--overlay-root" ref={overlayRef}></div>
    </Fragment>
  );
}

EasyblocksPage.propTypes = {
  pageId: pt.string,
  allSections: pt.array,
  pageSections: pt.array,
  layoutSectionGroups: pt.array,
  themeGlobals: pt.object,
};

export default React.memo(EasyblocksPage);
